import React, { useState } from 'react';
import FeatherIcon from 'feather-icons-react';

const relationColors = {
  1: '#2A7BEC', 
  2: '#FD800C', 
  3: '#9747FF', 
  4: '#00C853',
  default: '#2A7BEC'
};

const getRelationColor = (depth) => {
  return relationColors[depth] || relationColors.default;
};

const HierarchicalPrivilegeTable = ({ 
    privileges = [], 
    onPrivilegeSelect,
    onActionSelect,
    onEditPrivilege,
    onDeletePrivilege,
    onAddSubPrivilege,
    showActions = false,
    showCheckboxes = false,
    showExpandCollapse = true,
    mode = 'view',
    expandIconSize = 28,
    defaultExpandedIds,
}) => {
    const [expandedRows, setExpandedRows] = useState({});

    const idsKey = Array.isArray(defaultExpandedIds) ? defaultExpandedIds.join(',') : null;
    React.useEffect(() => {
        if (!Array.isArray(defaultExpandedIds)) return; 
        if (defaultExpandedIds.length > 0) {
            const initial = {};
            defaultExpandedIds.forEach(id => { initial[id] = true; });
            setExpandedRows(initial);
        } else {
            setExpandedRows({});
        }
    }, [idsKey]);

    const handleToggleExpand = (privilegeId, path = []) => {
        setExpandedRows(prevState => {
            const expandKey = path.length > 0 ? `${path.join('-')}-${privilegeId}` : privilegeId;

            if (prevState[expandKey]) {
                const newState = { ...prevState };
                delete newState[expandKey];
                return newState;
            }

            return {
                ...prevState,
                [expandKey]: true
            };
        });
    };

    const isLastItem = (items, index) => {
        return index === items.length - 1;
    };

    const getRowStyle = (isExpanded, level, currentColor) => {
        if (isExpanded && level === 0) {
            return {
                backgroundColor: mode === 'privilege' ? '#FF0000 !important' : '#5895E9 !important',
                color: 'white !important'
            };
        } else if (level > 0) {
            const hexColor = currentColor.replace('#', '');
            const r = parseInt(hexColor.substr(0, 2), 16);
            const g = parseInt(hexColor.substr(2, 2), 16);
            const b = parseInt(hexColor.substr(4, 2), 16);
            
            return {
                backgroundColor: `rgba(${r}, ${g}, ${b}, 0.1) !important`
            };
        }
        
        return {};
    };

    const getIconColor = (isExpanded, level) => {
        if (isExpanded && level === 0) {
            return { color: 'white !important' };
        }
        return {};
    };

    const getRowClassName = (isExpanded, level) => {
        let className = '';
        
        if (isExpanded && level === 0) {
            className += ' expanded-row-parent';
        } else if (level > 0) {
            className += ' nested-row';
            className += ` nested-level-${level}`;
        }
        
        return className;
    };

    const TreeLines = ({ level, isLast, currentColor, siblingsCount, index }) => {
        if (level === 0) return null;

        const baseLeft = (level - 1) * 24 + 36;
        const extra = level > 1 ? (level - 1) * 16 : 0;
        const left = baseLeft + extra;
        const color = getRelationColor(level);

        return (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 0
            }}>
                <div style={{
                    position: 'absolute',
                    left: `${left}px`,
                    top: 0,
                    height: isLast ? '50%' : '100%',
                    width: '1.5px',
                    backgroundColor: color,
                    zIndex: 1000
                }} />

                <div style={{
                    position: 'absolute',
                    left: `${left}px`,
                    top: '22px',
                    width: '15px',
                    height: '1.5px',
                    backgroundColor: color,
                    transform: 'translateY(-1px)',
                    zIndex: 1000
                }} />
            </div>
        );
    };

    const renderPrivilegeRows = (privileges, level = 0, parentPath = []) => {
        return privileges.map((privilege, index) => {
            const currentPath = [...parentPath, privilege.id];
            const expandKey = currentPath.length > 1 ? `${parentPath.join('-')}-${privilege.id}` : privilege.id;
            const isExpanded = expandedRows[expandKey];
            const hasChildren = privilege.child && privilege.child.length > 0;
            const hasExpandableChildren = hasChildren && isExpanded;
            const isLast = isLastItem(privileges, index);
            const currentColor = getRelationColor(level);
            const rowStyle = getRowStyle(isExpanded, level, currentColor);
            const iconStyle = getIconColor(isExpanded, level);
            const rowClassName = getRowClassName(isExpanded, level);
            
            return (
                <React.Fragment key={`${expandKey}-${index}`}>
                    <tr 
                        className={`${mode === 'privilege' ? '' : 'privilege-row'} ${rowClassName}`}
                        style={rowStyle}
                    >
                        <td
                            className={`sa-table-data ${level > 0 ? 'sa-padding-left-nested' : 'sa-padding-left'}`}
                            style={{
                                position: 'relative',
                                verticalAlign: 'top',
                                overflow: 'visible',
                                ...(level > 0 && {
                                    '--nested-padding': `${level * 20 + 20}px`
                                }),
                                ...(isExpanded && level === 0 && { color: 'white' })
                            }}
                        >
                            <TreeLines level={level} isLast={isLast} currentColor={currentColor} siblingsCount={privileges.length} index={index} />
                            <div className="privilege-content">
                                <span style={{ 
                                    position: 'relative',
                                    left: `${(level * 20) + (level > 0 ? 25 : 10) + (level > 1 ? (level - 1) * 16 : 0)}px`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    paddingLeft: '4px',
                                }}>
                                    {showCheckboxes && (
                                        <input
                                            type="checkbox"
                                            checked={privilege.selected === true}
                                            onChange={() => onPrivilegeSelect && onPrivilegeSelect(privilege)}
                                            onClick={(e) => e.stopPropagation()}
                                            ref={el => { if (el) el.indeterminate = privilege.selected === 'partial'; }}
                                            disabled={mode === 'view'}
                                            className="sa-checkbox"
                                            style={isExpanded && level === 0 ? { accentColor: getRelationColor(1) } : {}}
                                        />
                                    )}
                                    <span className={showCheckboxes ? "sa-privilege-text" : ""} style={{ 
                                        textTransform: showCheckboxes ? "capitalize" : "none", paddingLeft: 2
                                    }}>
                                        {privilege.name?.replace(/_/g, " ")}
                                    </span>
                                </span>
                            </div>
                        </td>

                        {mode === 'privilege' && (
                            <>
                                <td
                                    className="sa-table-data"
                                    style={isExpanded && level === 0 ? { color: 'white' } : {}}
                                >
                                    <div className="sa-actions-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                                        {privilege.actions?.map((action, actionIndex) => (
                                            <div key={actionIndex} className="sa-action-label" style={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                            }}>
                                                <span className="sa-action-text">{typeof action === 'string' ? action : action.action_name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                <td 
                                    className={'sa-table-data'}
                                    style={isExpanded && level === 0 ? { color: 'white' } : {}}
                                > 
                                    {privilege.description}
                                </td>
                            </>
                        )}

                        {(mode === 'select' || mode === 'edit' || mode === 'view') && (
                            <td
                                className="sa-table-data"
                                style={isExpanded && level === 0 ? { color: 'white' } : {}}
                            >
                                <div className="sa-actions-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    {privilege.actions?.map((action, actionIndex) => (
                                        <label key={actionIndex} className="sa-action-label" style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '5px',
                                            fontSize: '0.9rem',
                                            cursor: mode === 'view' ? 'default' : 'pointer',
                                            marginBottom: 0,
                                            whiteSpace: 'nowrap'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={action.selected === true}
                                                onChange={() => onActionSelect && onActionSelect(privilege, action)}
                                                onClick={(e) => e.stopPropagation()}
                                                disabled={mode === 'view'}
                                                className="sa-action-checkbox"
                                                style={isExpanded && level === 0 ? { accentColor: getRelationColor(1) } : {}}
                                            />
                                            <span className="sa-action-text">{action.action_name}</span>
                                        </label>
                                    ))}
                                </div>
                            </td>
                        )}
                        
                        <td className={mode === 'privilege' ? 'sa-table-item-align' : 'sa-table-data sa-table-item-align'}>
                            {mode === 'privilege' ? (
                                <div className='sa-table-item-flex'>
                                    <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title="Edit">
                                        <FeatherIcon
                                            className="table-action"
                                            icon="edit"
                                            size={16}
                                            style={iconStyle}
                                            onClick={() => onEditPrivilege && onEditPrivilege(privilege)}
                                        />
                                    </div>
                                    <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title="Delete">
                                        <FeatherIcon
                                            className="text-red"
                                            icon="trash-2"
                                            size={16}
                                            onClick={() => onDeletePrivilege && onDeletePrivilege(privilege.id, privilege)}
                                            style={isExpanded && level === 0 ? { color: 'white' } : {}}
                                        />
                                    </div>
                                    <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title="Add Sub-Privilege">
                                        <FeatherIcon
                                            className="table-action"
                                            icon="plus"
                                            size={16}
                                            style={iconStyle}
                                            onClick={() => onAddSubPrivilege && onAddSubPrivilege(privilege.id)}
                                        />
                                    </div>
                                    {hasChildren && showExpandCollapse && (
                                        <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title="Toggle Children">
                                            <FeatherIcon
                                                className="table-action expand-toggle"
                                                icon={isExpanded ? "chevron-up" : "chevron-down"}
                                                size={16}
                                                onClick={() => handleToggleExpand(privilege.id, parentPath)}
                                                style={iconStyle}
                                            />                    
                                        </div>
                                    )}
                                </div>
                            ) : (
                                hasChildren && showExpandCollapse && (
                                    <div className="sa-table-actions sa-action-m">
                                        <FeatherIcon
                                            className="table-action expand-toggle"
                                            icon={isExpanded ? "chevron-up" : "chevron-down"}
                                            size={expandIconSize}
                                            onClick={() => handleToggleExpand(privilege.id, parentPath)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '5px',
                                                ...iconStyle
                                            }}
                                        />
                                    </div>
                                )
                            )}
                        </td>
                    </tr>
                    {hasExpandableChildren && renderPrivilegeRows(
                        privilege.child,
                        level + 1,
                        currentPath
                    )}
                </React.Fragment>
            );
        });
    };

    return (
        <>
            {renderPrivilegeRows(privileges)}
        </>
    );
};

export default HierarchicalPrivilegeTable;
